import { Task, Pulse, Message, PulseWithTasks } from "@shared/schema";
import { getLinkedShareFromPulse } from "@/lib/linked-share";
import { Button } from "@/components/ui/button";
import { ChevronDown, Eye, Mail, ExternalLink, Circle, MessageCircle, Square, CheckSquare, Paperclip, Milestone, Plus, Clock, Settings, Share2, FileImage, FileText, FileArchive, FileSpreadsheet, File as FileIcon, FileVideo, Coins, Lock, Unlock, Shield, Phone, Loader2, AlertTriangle } from "lucide-react";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { useAuth } from "@/contexts/auth-context";
import { EditableStatus } from "./editable-status";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useState, useMemo, useEffect, useCallback } from "react";
import { MessageModal } from "@/components/message-modal";
import { TaskDetailDialog } from "@/components/task-detail-dialog";
import { TaskEditButton } from "@/components/task-edit-button";
import { TaskDatePicker } from "@/components/task-date-picker";
import { PulseSequenceIndicators } from "@/components/pulse-sequence-indicators";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useDialogOpen } from "@/contexts/dialog-context";
import { usePulseById } from "@/hooks/useProjectTreeData";
import { usePulseWorkbenchContextSafe } from "@/contexts/pulse-workbench-context";
import { getMilestoneStyles } from "@/lib/milestone-utils";
import { getPulseTitleParts, stripHtmlToText } from "@/lib/pulse-link";
import { useScrollPreservation } from "@/hooks/use-scroll-preservation";
import { logger } from "@/services/debug-logger";
import { TREE_COLUMNS, TREE_GRID_TEMPLATE, TREE_GRID_TEMPLATE_WITH_BUDGET, getTreeColumnStyles } from "@/lib/tree-columns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PulseAttachmentPreviewModal, type PulseAttachment } from "@/components/pulse-attachment-preview-modal";
import { isPreviewable } from "@/components/ui/file-viewer";
import { BudgetPulseDetail } from "@/components/budget-pulse-detail";
import { PulseReactionDisplay, PulseReactionBar } from "@/components/pulse-reactions";
import { PreviewShareLink } from "@/components/preview-share-link";
import { ClientUploadReview } from "@/components/client-upload-review";
import { PulseUserTags } from '@/components/pulse-user-tags';
import { usePulseTagRoster, sendPulseTag, recordPulseRead } from '@/lib/pulse-user-tags';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { isClientUploadPulse } from "@shared/client-uploads";
import { TimeTrackButton } from "@/components/timetrack";
import { TimeTrackSummary } from "@/components/timetrack-summary";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return FileImage;
  if (contentType.startsWith('video/')) return FileVideo;
  if (contentType === 'application/pdf') return FileText;
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed')) return FileArchive;
  if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return FileSpreadsheet;
  return FileIcon;
}

interface TaskRowProps {
  task: (Task | Pulse) & { childTasks?: any[]; tasks?: any[]; message?: Message };
  projectId: string;
  depth: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isPulse?: boolean;
  onAddTaskClick?: () => void; // New prop for triggering inline task creation
  pulseSummary?: {
    hasTasks: boolean;
    totals: {
      all: number;
      done: number;
      working: number;
      other: number;
    };
  }; // Batch-loaded pulse summary data
  pulseMetadata?: { // Batch-loaded metadata from batch endpoint
    hasTasks: boolean;
    sequences: any[];
    timeTracking: {
      totalMinutes: number;
      byType: Array<{ timeTypeId: string; totalMinutes: number }>;
    };
  };
  isBatchContext?: boolean; // FIXED: Direct boolean flag for batch context (synchronous, no timing issues)
  onTrackTime?: (event: React.MouseEvent) => void; // Callback for time tracking popup (state lifted to parent)
  shouldShowTimeTracking?: boolean; // Computed once in parent to avoid N+1 queries
  onOpenSettings?: (pulse: Pulse) => void;
  onShareClick?: (pulse: Pulse) => void;
  showBudgetColumn?: boolean;
  pulseReactions?: any[];
}

export function TaskRow({
  task,
  projectId,
  depth,
  isExpanded,
  onToggleExpanded,
  isPulse = false,
  onAddTaskClick,
  pulseSummary,
  pulseMetadata,
  isBatchContext = false,
  onTrackTime,
  shouldShowTimeTracking = false,
  onOpenSettings,
  onShareClick,
  showBudgetColumn = false,
  pulseReactions
}: TaskRowProps) {
  const queryClient = useQueryClient();
  const workbenchContext = usePulseWorkbenchContextSafe();
  const { user: currentUser } = useAuth();
  const tagUsers = usePulseTagRoster();
  const isAdminUser = currentUser?.role === 'admin';

  // Task #121: per-pulse visibility toggle (admin-only). Lets admins flip a
  // single email pulse between public and admin_only without changing the
  // project-level mode. The lock is independent of the project's
  // communicationMode and the pulse's category.
  const togglePulseVisibilityMutation = useMutation({
    mutationFn: async () => {
      const current = (task as any).visibility === 'admin_only' ? 'admin_only' : 'public';
      const next = current === 'admin_only' ? 'public' : 'admin_only';
      const res = await apiRequest('PATCH', `/api/pulses/${task.id}/visibility`, { visibility: next });
      if (!res.ok) throw new Error('Failed to update pulse visibility');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId] });
    },
  });

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [attachmentPopoverOpen, setAttachmentPopoverOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<PulseAttachment | null>(null);
  const [previewPulseId, setPreviewPulseId] = useState<number | null>(null);
  const { captureScroll, restoreScroll } = useScrollPreservation();

  // Generate unique dialog ID for this task/pulse - include type to prevent ID conflicts
  const dialogId = isPulse ? `pulse-detail-${task.id}` : `task-detail-${task.id}`;
  
  // Use reactive dialog hook - only re-renders when THIS specific dialog's state changes
  const [showTaskDetail, setShowTaskDetail] = useDialogOpen(dialogId);
  
  // Time tracking categories (ordered: Production, Changes, Other, Problem-Solving)
  const timeCategories = [
    { id: 'production', name: 'Production', color: '#6989c4' },
    { id: 'changes', name: 'Changes', color: '#3e5275' },
    { id: 'other', name: 'Other', color: '#374151' },
    { id: 'problem_solving', name: 'Problem', color: '#ff8357' }
  ];

  // NEW UNIFIED APPROACH: Get all pulse data from tree cache
  const pulseTreeData = isPulse ? usePulseById(task.id) : null;
  
  // Extract data from tree (prioritize tree data, then fall back to props for backward compatibility)
  const hasTasks = pulseTreeData 
    ? { hasTasks: pulseTreeData.hasTasks } 
    : pulseMetadata 
    ? { hasTasks: pulseMetadata.hasTasks } 
    : pulseSummary 
    ? { hasTasks: pulseSummary.hasTasks } 
    : null;
  
  const finalTimeTracking = pulseTreeData?.timeTracking || pulseMetadata?.timeTracking || null;
  const taskCounts = pulseTreeData?.taskCounts || pulseSummary?.totals || null;
  
  // Determine if expand button should be shown
  const hasChildren = isPulse && hasTasks?.hasTasks === true;
  
  // Check if we should show the "+Add Task" button on hover
  const canAddTask = isPulse && 
    task.category === 'internal' && 
    hasTasks?.hasTasks === false &&
    !!onAddTaskClick;
  
  const showAddTaskButton = canAddTask && isHovered;
  
  const indentClass = `tree-indent-${Math.min(depth, 4)}`;
  
  // Fetch pulse details only when expanded (PulseTreeData doesn't include task array)
  const { data: pulseDetails } = useQuery<PulseWithTasks>({
    queryKey: ['/api/pulses', task.id, 'details'],
    enabled: isPulse && isExpanded && hasTasks?.hasTasks === true,
    staleTime: 60 * 1000,
  });

  // Calculate task progress for pulses
  const taskProgress = useMemo(() => {
    // Priority 1: Use task counts from tree data or props (most efficient, no need to count)
    if (isPulse && taskCounts) {
      const totalTasks = taskCounts.all;
      
      if (totalTasks === 0) return null;
      
      const donePercent = (taskCounts.done / totalTasks) * 100;
      const workingPercent = (taskCounts.working / totalTasks) * 100;
      const otherPercent = (taskCounts.other / totalTasks) * 100;
      
      return {
        done: donePercent,
        working: workingPercent,
        other: otherPercent,
        hasTasks: true
      };
    }
    
    // Priority 2: Fall back to detailed task data when expanded (for real-time updates)
    // Note: PulseTreeData doesn't include tasks array, only taskCounts
    if (!isPulse || !pulseDetails?.tasks || pulseDetails.tasks.length === 0) {
      return null;
    }

    const totalTasks = pulseDetails.tasks.length;
    let doneCount = 0;
    let workingCount = 0;
    let otherCount = 0;

    // Count tasks in each category
    pulseDetails.tasks.forEach((t: any) => {
      if (t.status === 'done') {
        doneCount++;
      } else if (t.status === 'working_on_it') {
        workingCount++;
      } else {
        otherCount++;
      }
    });

    // Calculate percentages
    const donePercent = (doneCount / totalTasks) * 100;
    const workingPercent = (workingCount / totalTasks) * 100;
    const otherPercent = (otherCount / totalTasks) * 100;

    return {
      done: donePercent,
      working: workingPercent,
      other: otherPercent,
      hasTasks: true
    };
  }, [isPulse, taskCounts, pulseDetails?.tasks]);

  // First-line title for the row, guarded against legacy/bad rows whose
  // description carries raw HTML (e.g. Word/Outlook emails whose "plain text"
  // part was actually HTML). stripHtmlToText is a no-op for normal plain-text
  // descriptions, so chat/call/task/normal-email titles are unaffected.
  const taskDescriptionRaw = 'taskDescription' in task ? task.taskDescription : null;
  const pulseDescriptionRaw = 'pulseDescription' in task ? task.pulseDescription : null;
  const cleanBaseLabel = useMemo(() => {
    const description = taskDescriptionRaw || pulseDescriptionRaw;
    const firstLine = description?.split('\n')[0] || description || '';
    return stripHtmlToText(firstLine);
  }, [taskDescriptionRaw, pulseDescriptionRaw]);

  const updateTaskMutation = useMutation({
    mutationFn: async (updates: { status?: string; dueDate?: string | null }) => {
      const endpoint = isPulse ? `/api/pulses/${task.id}` : `/api/tasks/${task.id}`;
      logger.debug('TASK_ROW', `Updating ${isPulse ? 'pulse' : 'task'} ${task.id}`, { updates });
      
      try {
        const response = await apiRequest('PATCH', endpoint, updates);
        const data = await response.json();
        logger.debug('TASK_ROW', `Update successful for ${isPulse ? 'pulse' : 'task'} ${task.id}`, { data });
        return data;
      } catch (error) {
        console.error(`Failed to update ${isPulse ? 'pulse' : 'task'} ${task.id}:`, error);
        throw error;
      }
    },
    onMutate: async (updates) => {
      // Capture scroll position immediately before any updates
      captureScroll();
      
      // CRITICAL FIX: Cancel ALL outgoing refetches to prevent race conditions and date reversions  
      await queryClient.cancelQueries({ queryKey: ['/api/years'] });
      await queryClient.cancelQueries({ queryKey: ['/api/projects'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses/batch-details'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses/batch-metadata'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses/batch/summary'] });
      await queryClient.cancelQueries({ queryKey: ['/api/time-tracking/batch'] });
      
      // Cancel pulse-specific queries for both pulses and tasks
      if (isPulse) {
        // For pulse updates, cancel pulse details and project pulses queries
        await queryClient.cancelQueries({ queryKey: ['/api/pulses', task.id, 'details'] });
        await queryClient.cancelQueries({ queryKey: [`/api/projects/${projectId}/pulses`] });
      } else if ((task as any).pulseId) {
        // For task updates, cancel pulse details query
        await queryClient.cancelQueries({ queryKey: ['/api/pulses', (task as any).pulseId, 'details'] });
      }

      // Snapshot the previous values
      const previousYears = queryClient.getQueryData(['/api/years']);
      let previousPulseDetails: any = null;
      const pulseId = !isPulse ? (task as any).pulseId : null;
      
      if (pulseId) {
        previousPulseDetails = queryClient.getQueryData(['/api/pulses', pulseId, 'details']);
      }

      // Helper function to update tasks (no longer recursive since subtasks removed)
      const updateTaskRecursively = (tasks: any[]): any[] => {
        if (!tasks) return tasks;
        return tasks.map(t => {
          if (t.id === task.id) {
            // Create a new object with the updates
            return { ...t, ...updates };
          }
          return t;
        });
      };

      // Optimistically update the years cache
      queryClient.setQueryData(['/api/years'], (old: any) => {
        if (!old) return old;
        
        // Create a new array with updated data
        return old.map((year: any) => ({
          ...year,
          projects: year.projects?.map((project: any) => ({
            ...project,
            pulses: project.pulses?.map((pulse: any) => {
              if (isPulse && pulse.id === task.id) {
                // Update the pulse itself
                return { ...pulse, ...updates };
              }
              // Update tasks within pulses
              return {
                ...pulse,
                tasks: !isPulse ? updateTaskRecursively(pulse.tasks) : pulse.tasks
              };
            })
          }))
        }));
      });

      // Also optimistically update the pulse details cache if this is a task
      if (pulseId) {
        queryClient.setQueryData(['/api/pulses', pulseId, 'details'], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            tasks: updateTaskRecursively(old.tasks)
          };
        });
      }

      // Return context with snapshots
      return { previousYears, previousPulseDetails, pulseId };
    },
    onError: (err, newData, context) => {
      // Log the error for debugging
      console.error('Status update failed:', err);
      
      // If mutation fails, use the context to roll back
      if (context?.previousYears) {
        queryClient.setQueryData(['/api/years'], context.previousYears);
      }
      if (context?.previousPulseDetails && context?.pulseId) {
        queryClient.setQueryData(['/api/pulses', context.pulseId, 'details'], context.previousPulseDetails);
      }
      
      // Show error toast
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      console.error('Status update error details:', errorMessage);
    },
    onSuccess: (data) => {
      // Update the pulse details query cache directly with the new task data
      if (!isPulse && (task as any).pulseId) {
        const pulseId = (task as any).pulseId;
        queryClient.setQueryData(['/api/pulses', pulseId, 'details'], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            tasks: old.tasks?.map((t: any) => {
              if (t.id === task.id) {
                return data; // Use the response data from the server
              }
              // Check nested tasks recursively
              const updateNestedTasks = (tasks: any[]): any[] => {
                if (!tasks) return tasks;
                return tasks.map(nt => {
                  if (nt.id === task.id) {
                    return data;
                  }
                  return {
                    ...nt,
                    childTasks: updateNestedTasks(nt.childTasks)
                  };
                });
              };
              return {
                ...t,
                childTasks: updateNestedTasks(t.childTasks)
              };
            })
          };
        });
      }
      
      // Update pulse data in cache if this is a pulse
      if (isPulse) {
        logger.debug('TASK_ROW', `Updating caches after successful pulse ${task.id} status update`, { status: data.status });
        
        // 1. CRITICAL FIX: Update the batch-details cache that the UI actually reads from
        // This is the main cache used by VirtualizedPulseList to display pulse rows
        const batchDetailsQueries = queryClient.getQueriesData({ 
          queryKey: ['/api/pulses/batch-details'] 
        });
        
        batchDetailsQueries.forEach(([queryKey, queryData]) => {
          if (queryData && Array.isArray(queryData)) {
            const keyArray = queryKey as any[];
            const pulseIds = keyArray[1];
            
            // Check if this batch includes our pulse
            if (Array.isArray(pulseIds) && pulseIds.includes(task.id)) {
              logger.debug('TASK_ROW', `Updating batch-details cache for pulse ${task.id}`, { queryKey });
              
              // Update the pulse in this batch with new status AND dueDate
              const updatedData = queryData.map((pulse: any) => 
                pulse.id === task.id 
                  ? { ...pulse, ...data } // Use all returned data including status and dueDate
                  : pulse
              );
              
              queryClient.setQueryData(queryKey, updatedData);
            }
          }
        });
        
        // 2. Update the pulse in the project's pulses list (used by ProjectTree)
        queryClient.setQueryData([`/api/projects/${projectId}/pulses`], (old: any) => {
          if (!old || !old.data) return old;
          logger.debug('TASK_ROW', `Updating project pulses cache for pulse ${task.id}`);
          return {
            ...old,
            data: old.data.map((p: any) => 
              p.id === task.id ? { ...p, ...data } : p
            )
          };
        });

        // 2b. CRITICAL FIX: Also update the paginated pulses cache used by ProjectTree
        queryClient.setQueryData(['/api/projects', projectId, 'pulses', 'paginated'], (old: any) => {
          if (!old || !old.data) return old;
          logger.debug('TASK_ROW', `Updating paginated project pulses cache for pulse ${task.id}`);
          return {
            ...old,
            data: old.data.map((p: any) => 
              p.id === task.id ? { ...p, ...data } : p
            )
          };
        });

        // 3. Update individual pulse details if cached (for detail views)
        queryClient.setQueryData(['/api/pulses', task.id, 'details'], (old: any) => {
          if (!old) return old;
          logger.debug('TASK_ROW', `Updating pulse details cache for pulse ${task.id}`);
          return { ...old, ...data };
        });
        
        // 4. Update the years query cache WITHOUT invalidating it
        queryClient.setQueryData(['/api/years'], (old: any) => {
          if (!old) return old;
          
          return old.map((year: any) => ({
            ...year,
            projects: year.projects?.map((project: any) => {
              if (project.id === projectId) {
                return {
                  ...project,
                  pulses: project.pulses?.map((pulse: any) =>
                    pulse.id === task.id ? { ...pulse, ...data } : pulse
                  )
                };
              }
              return project;
            })
          }));
        });
        
        // 5. Invalidate batch details as a safety net to ensure fresh data
        // This targets any batch-details query that includes this pulse
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return queryKey[0] === '/api/pulses/batch-details' &&
                   Array.isArray(queryKey[1]) && 
                   queryKey[1].includes(task.id);
          }
        });
        
        logger.debug('TASK_ROW', `Cache updates complete for pulse ${task.id}`);
        
        // 6. Only invalidate the specific pulse details query to refresh backend data
        // This is done after a delay to avoid UI flicker
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['/api/pulses', task.id, 'details']
          });
        }, 100);
      }
      
      // Restore scroll immediately after successful mutation
      restoreScroll();
    },
    onSettled: () => {
      // DON'T invalidate /api/years immediately as it causes UI reset
      // The cache has already been updated in onSuccess
      
      // Also invalidate pulse details queries for tasks within pulses
      if (!isPulse && (task as any).pulseId) {
        const pulseId = (task as any).pulseId;
        queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'details'] });
        
        // Invalidate progress queries for progress bars
        queryClient.invalidateQueries({ queryKey: [`/api/pulses/${pulseId}/progress`] });
      }
      
      // For pulse status updates, invalidate its own progress
      if (isPulse) {
        // Invalidate progress queries for progress bars
        queryClient.invalidateQueries({ queryKey: [`/api/pulses/${task.id}/progress`] });
      }
    },
  });

  const handleTaskStatusChange = (newStatus: string) => {
    // Scroll position is captured in onMutate and restored in onSuccess
    updateTaskMutation.mutate({ status: newStatus });
  };

  const handlePulseStatusChange = (newStatus: string) => {
    // Scroll position is captured in onMutate and restored in onSuccess
    updateTaskMutation.mutate({ status: newStatus });
  };

  const handleDueDateChange = (taskId: number, dueDate: string | null) => {
    // API expects camelCase field name. An empty/null value must be sent as an
    // explicit null: `undefined` is dropped from the JSON body, so the clear
    // would only ever be applied optimistically and reappear on refetch.
    updateTaskMutation.mutate({ dueDate: dueDate ? dueDate : null });
  };

  // Check if this is a milestone
  const isMilestone = isPulse && task.category === 'milestone';
  const milestoneColor = (task as any).milestoneColor;
  
  // Check if this is a non-clickable pulse (system only). Preview pulses
  // became clickable with task #29 — they open the client share manager.
  // Task #189: final pulses also open the share manager (manage mode).
  const isNonClickablePulse = isPulse && task.category === 'system';
  const isPreviewPulse = isPulse && task.category === 'preview';
  const isFinalPulse = isPulse && task.category === 'final';
  const isClientFilesPulse = isPulse && isClientUploadPulse(task);
  const [showClientUploadReview, setShowClientUploadReview] = useState(false);
  const clientUploadSessionId = isClientFilesPulse && 'metadata' in task &&
    typeof (task.metadata as Record<string, unknown> | null)?.clientUploadSessionId === 'string'
    ? (task.metadata as Record<string, unknown>).clientUploadSessionId as string : undefined;
  const isShareManagerPulse = isPreviewPulse || isFinalPulse;
  // Do not trust raw metadata: the server validates delivery and BOTH pulses'
  // permissions before exposing these top-level linked fields.
  const linkedShare = isPulse
    ? getLinkedShareFromPulse(task)
    : null;
  const hasShareThumbnail = isShareManagerPulse || !!linkedShare;
  
  // Category icon component
  const getCategoryIcon = (category: string) => {
    // For tasks (not pulses), show checkbox
    if (!isPulse) {
      const isChecked = task.status === 'done' || task.status === 'Done';
      return (
        <IconTooltip label={isChecked ? "Mark as open" : "Mark as done"}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault(); // Prevent any default scroll behavior
              handleTaskStatusChange(isChecked ? 'working_on_it' : 'done');
            }}
            className="p-0 hover:opacity-70 transition-opacity"
            aria-label={isChecked ? "Mark as open" : "Mark as done"}
          >
            {isChecked ? (
              <CheckSquare className="w-4 h-4 text-green-500" />
            ) : (
              <Square className="w-4 h-4 text-gray-400" />
            )}
          </button>
        </IconTooltip>
      );
    }
    
    // Task #145: admin-category (system/audit) pulses always show a Shield icon
    // for admin viewers — this check must run BEFORE the admin_only Lock branch
    // so an admin-category pulse never falls through to the lock marker.
    if (isAdminUser && task.category === 'admin') {
      return (
        <span
          title="System/Admin Audit Pulse"
          aria-label="System/Admin Audit Pulse"
          className="inline-flex items-center justify-center"
        >
          <Shield className="w-3 h-3 text-amber-400" />
        </span>
      );
    }

    // Task #122: for admins, when a regular-category pulse is locked admin_only,
    // replace the category dot with a lock marker so the visibility status is
    // obvious. Non-admins never see admin_only pulses (filtered server-side).
    if (isAdminUser && (task as any).visibility === 'admin_only') {
      return (
        <span
          title="Admin-Only Visibility"
          aria-label="Admin-Only Visibility"
          className="inline-flex items-center justify-center"
        >
          <Lock className="w-3 h-3 text-amber-400" />
        </span>
      );
    }

    // For pulses, show category icons - all with uniform size
    switch (category) {
      case 'email_in':
        return <Mail className="w-3 h-3" style={{ color: '#936fda' }} />;
      case 'email_out':
        return <Mail className="w-3 h-3" style={{ color: '#5f8ac9' }} />;
      case 'internal':
        return <Circle className="w-3 h-3 fill-current" style={{ color: '#5f8ac9' }} />;
      case 'technical':
        return <Circle className="w-3 h-3 fill-current" style={{ color: '#5c5c5c' }} />;
      case 'chat':
        return <MessageCircle className="w-3 h-3" style={{ color: '#b65d3f' }} />;
      case 'call':
        // Phase E.2 (#192): call pulses get a phone icon in the timeline.
        return <Phone className="w-3 h-3" style={{ color: '#34d391' }} />;
      case 'milestone':
        return <Milestone className="w-3 h-3" style={{ color: milestoneColor || '#8d70d3' }} />;
      case 'effort':
        return <Coins className="w-3 h-3" style={{ color: '#10b981' }} />;
      case 'preview':
        return <Eye className="w-3 h-3" style={{ color: '#5f8ac9' }} />;
      case 'client_files':
        return <Paperclip className="w-3 h-3" style={{ color: '#8baed8' }} />;
      case 'final':
        // Task #189: final pulses use a green check-circle icon.
        return <Eye className="w-3 h-3" style={{ color: '#34d391' }} />;
      case 'system':
        return <Circle className="w-3 h-3 fill-current" style={{ color: '#8d70d3' }} />;
      default:
        return <Circle className="w-3 h-3 fill-current" style={{ color: '#34d391' }} />;
    }
  };

  return (
    <>
      <div 
        className={cn(
          "tree-item border-b border-gray-600",
          hasShareThumbnail && "flex flex-col",
          !isPulse && "task-row-bg", // Add darker background for tasks only
          indentClass
        )}
        style={isMilestone ? getMilestoneStyles(milestoneColor) : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <div className={cn("grid px-3 items-center gap-0", hasShareThumbnail && "flex-1")} style={{ gridTemplateColumns: showBudgetColumn ? TREE_GRID_TEMPLATE_WITH_BUDGET : TREE_GRID_TEMPLATE, ...getTreeColumnStyles(showBudgetColumn) }}>
          {/* Thumbnail Placeholder Column */}
          <div className="flex items-center justify-center border-r border-gray-600" />
          
          {/* Expand/Collapse Column */}
          <div className="flex items-center justify-center">
            {hasChildren && (
              <Button
                variant="ghost"
                size="sm"
                className="expandable-row p-0 h-auto"
                onClick={onToggleExpanded}
              >
                <ChevronDown
                  className={cn(
                    "w-4 h-4 text-gray-400 transition-transform expand-icon",
                    isExpanded ? "expanded" : "collapsed"
                  )}
                />
              </Button>
            )}
            {showAddTaskButton && (
              <IconTooltip label="Add subtask">
                <button
                  className="p-0.5 bg-blue-500/20 hover:bg-blue-500/30 rounded transition-all flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddTaskClick?.();
                  }}
                  title="Add subtask"
                  aria-label="Add subtask"
                >
                  <Plus className="w-3 h-3 text-blue-400" />
                </button>
              </IconTooltip>
            )}
          </div>
          
          {/* Task ID Column */}
          <div className="flex items-center">
            {isPulse ? (
              <PulseSequenceIndicators 
                pulseId={task.id} 
                projectId={projectId}
                className="ml-1"
                sequences={pulseMetadata ? (pulseMetadata.sequences || []) : undefined}
              />
            ) : (
              // For tasks, show the pulse's sequence indicators if task belongs to a pulse
              (task as any).pulseId && (
                <PulseSequenceIndicators 
                  pulseId={(task as any).pulseId} 
                  projectId={projectId}
                  className="ml-1"
                  sequences={pulseMetadata ? (pulseMetadata.sequences || []) : undefined}
                />
              )
            )}
          </div>

          {/* Task Description Column */}
          <div className={cn("min-w-0 px-3", hasShareThumbnail && "self-stretch flex flex-col")}>
            <div className={cn("flex flex-col", hasShareThumbnail && "flex-1")}>
              <div className={cn("flex items-center", hasShareThumbnail && "flex-1")}>
                {/* Fixed width icon container for consistent alignment */}
                <div className="w-5 mr-2 flex justify-center">
                  {!isMilestone && getCategoryIcon(task.category || 'internal')}
                </div>
                <button
                  data-testid={`${isPulse ? 'pulse' : 'task'}-description-${task.id}`}
                  onClick={(e) => {
                    // Don't open dialog for non-clickable pulses
                    if (isNonClickablePulse) return;
                    if (isPulse) void recordPulseRead(task.id);
                    
                    e.stopPropagation();
                    logger.debug('TASK_ROW', 'Task description clicked', { taskId: task.id, category: task.category, isPulse });
                    if (isClientFilesPulse) {
                      setShowClientUploadReview(true);
                    } else if (isShareManagerPulse) {
                      // Task #29/#189: preview and final pulses open the client share manager.
                      window.dispatchEvent(new CustomEvent('open-preview-share', { detail: { pulseId: task.id } }));
                    } else if (task.category === 'chat' && isPulse) {
                      setShowChatDialog(true);
                    } else if (isPulse && workbenchContext) {
                      // Use new Pulse Workbench for pulses
                      workbenchContext.openWorkbench(task.id);
                    } else {
                      // Fallback to legacy dialog for tasks or if workbench not available
                      setShowTaskDetail(true);
                    }
                  }}
                  className={cn(
                    "transition-colors text-left flex items-center gap-1 min-w-0 flex-1",
                    isMilestone ? "font-bold text-base" : "text-sm",
                    isNonClickablePulse 
                      ? "text-gray-500 cursor-default opacity-60" 
                      : "hover:text-blue-400 cursor-pointer"
                  )}
                >
                  {(() => {
                    const baseLabel = isClientFilesPulse ? 'Files from Client' : cleanBaseLabel;
                    const pulseMeta: Record<string, unknown> | null | undefined =
                      isPulse && 'metadata' in task ? (task as Pulse).metadata : undefined;
                    const titleParts = isPulse ? getPulseTitleParts(baseLabel, pulseMeta) : null;
                    // Phase E.2 (#192): call pulses get a compact suffix
                    // showing duration + participant count in the collapsed
                    // timeline row, so users can scan the timeline without
                    // opening the dialog. The suffix is appended AFTER the
                    // (possibly link-spliced) title so the inline anchor and
                    // its friendly label still read as one sentence.
                    let suffix = '';
                    if (isPulse && task.category === 'call') {
                      const dur = pulseMeta && typeof pulseMeta.durationSeconds === 'number' ? pulseMeta.durationSeconds : null;
                      const rawParticipantIds = pulseMeta?.participantUserIds;
                      const pCount = Array.isArray(rawParticipantIds) ? rawParticipantIds.length : null;
                      const bits: string[] = [];
                      if (dur != null && dur > 0) {
                        const m = Math.floor(dur / 60);
                        const s = dur % 60;
                        bits.push(`${m}:${s.toString().padStart(2, '0')} min`);
                      }
                      if (pCount != null && pCount > 0) {
                        bits.push(`${pCount} Tn`);
                      }
                      if (bits.length > 0) {
                        suffix = `  ·  ${bits.join(' · ')}`;
                      }
                    }
                    if (titleParts) {
                      const isUrlOnly =
                        titleParts.before.trim() === '' &&
                        titleParts.after.trim() === '';
                      return (
                        <span className="truncate block min-w-0">
                          {isUrlOnly && (
                            <span
                              className="text-gray-400 mr-1"
                              data-testid={`pulse-link-prefix-${task.id}`}
                            >
                              Link:{' '}
                            </span>
                          )}
                          {titleParts.before}
                          <a
                            href={titleParts.hrefUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={titleParts.hrefUrl}
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-400 hover:underline"
                            data-testid={`pulse-link-${task.id}`}
                          >
                            {titleParts.label}
                          </a>
                          {titleParts.after}
                          {suffix}
                        </span>
                      );
                    }
                    return (
                      <span className="truncate block min-w-0">{baseLabel}{suffix}</span>
                    );
                  })()}
                  {task.attachments && task.attachments.length > 0 && !isPulse && (
                    <span title={`${task.attachments.length} attachment${task.attachments.length > 1 ? 's' : ''}`} className="flex-shrink-0">
                      <Paperclip className="w-3.5 h-3.5 text-blue-400/70" />
                    </span>
                  )}
                  {task.attachments && task.attachments.length > 0 && isPulse && (
                    <Popover open={attachmentPopoverOpen} onOpenChange={setAttachmentPopoverOpen}>
                      <PopoverTrigger asChild>
                        <span
                          title={`${task.attachments.length} attachment${task.attachments.length > 1 ? 's' : ''}`}
                          className="flex-shrink-0 cursor-pointer hover:text-blue-300 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAttachmentPopoverOpen(true);
                          }}
                        >
                          <Paperclip className="w-3.5 h-3.5 text-blue-400/70 hover:text-blue-300" />
                        </span>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-72 p-0 bg-gray-900 border-gray-700"
                        align="start"
                        side="bottom"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-3 py-2 border-b border-gray-700">
                          <span className="text-xs font-medium text-gray-400">
                            {task.attachments.length} Attachment{task.attachments.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {task.attachments.map((att: any, idx: number) => {
                            const Icon = getFileIcon(att.contentType || 'application/octet-stream');
                            const canPreview = isPreviewable(att.contentType || '');
                            // Mirror the pulse workbench attachment status treatment:
                            // pending → spinner, failed/source_missing → warning and
                            // not downloadable (see workbench-shell.tsx).
                            const attStatus = att.status || 'uploaded';
                            const isAttPending = attStatus === 'pending';
                            const isAttFailed = attStatus === 'failed' || attStatus === 'source_missing';
                            const isAttReady = !isAttPending && !isAttFailed;
                            const attStatusLabel = attStatus === 'source_missing'
                              ? 'Source unavailable'
                              : attStatus === 'failed'
                                ? 'Upload failed'
                                : 'Uploading…';
                            if (!isAttReady) {
                              return (
                                <div
                                  key={idx}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left cursor-default"
                                  title={attStatusLabel}
                                  data-testid={`popover-attachment-unavailable-${idx}`}
                                >
                                  {isAttPending ? (
                                    <Loader2 className="w-4 h-4 text-amber-400 animate-spin flex-shrink-0" />
                                  ) : (
                                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                  )}
                                  <span className="truncate flex-1 text-gray-400">{att.filename}</span>
                                  <span className={`flex-shrink-0 ${isAttFailed ? 'text-red-400' : 'text-amber-400'}`}>
                                    {attStatusLabel}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <button
                                key={idx}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-800 transition-colors text-left"
                                onClick={async () => {
                                  setAttachmentPopoverOpen(false);
                                  if (canPreview) {
                                    setPreviewPulseId(task.id);
                                    setPreviewAttachment(att as PulseAttachment);
                                  } else {
                                    try {
                                      const url = `/api/pulses/${task.id}/attachments/download?path=${encodeURIComponent(att.path)}`;
                                      const response = await fetch(url, {
                                        credentials: 'include'
                                      });
                                      if (!response.ok) throw new Error('Download failed');
                                      const blob = await response.blob();
                                      const downloadUrl = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = downloadUrl;
                                      a.download = att.filename;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      URL.revokeObjectURL(downloadUrl);
                                    } catch (err) {
                                      console.error('Download failed:', err);
                                    }
                                  }
                                }}
                              >
                                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="truncate flex-1 text-gray-200">{att.filename}</span>
                                <span className="text-gray-500 flex-shrink-0">{formatFileSize(att.size || 0)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  {isPulse && pulseReactions && pulseReactions.length > 0 && (
                    <span className="flex-shrink-0 ml-0.5" onClick={(e) => e.stopPropagation()}>
                      <PulseReactionDisplay
                        reactions={pulseReactions}
                        pulseId={task.id}
                        projectId={projectId}
                        maxVisible={3}
                        compact={true}
                      />
                    </span>
                  )}
                  {isPulse && isHovered && (
                    <span className="flex-shrink-0 ml-0.5" onClick={(e) => e.stopPropagation()}>
                      <PulseReactionBar
                        pulseId={task.id}
                        projectId={projectId}
                        className="h-4 w-4"
                        side="bottom"
                      />
                    </span>
                  )}
                  {isPulse && isAdminUser && task.category !== 'chat' && task.category !== 'admin' && (
                    // NOTE: rendered as <span role="button"> (not <button>) because the
                    // surrounding pulse-row click target is itself a <button>, and nesting
                    // <button> inside <button> is invalid DOM. React's reconciliation breaks
                    // on this, throws spurious hook-call errors, and aborts rendering the
                    // remainder of the expanded pulse list (Task #121 regression).
                    <span
                      role="button"
                      tabIndex={0}
                      aria-disabled={togglePulseVisibilityMutation.isPending || undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (togglePulseVisibilityMutation.isPending) return;
                        togglePulseVisibilityMutation.mutate();
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (togglePulseVisibilityMutation.isPending) return;
                        togglePulseVisibilityMutation.mutate();
                      }}
                      className={cn(
                        "inline-flex items-center justify-center flex-shrink-0 ml-1 p-0.5 rounded transition-colors cursor-pointer",
                        togglePulseVisibilityMutation.isPending && "opacity-50 cursor-not-allowed",
                        (task as any).visibility === 'admin_only'
                          ? "text-amber-400 hover:bg-amber-900/30"
                          : "text-gray-500 hover:text-amber-400 opacity-0 group-hover:opacity-100"
                      )}
                      title={(task as any).visibility === 'admin_only'
                        ? 'Make visible to all'
                        : 'Make admin-only'}
                      aria-label={(task as any).visibility === 'admin_only'
                        ? 'Make visible to all'
                        : 'Make admin-only'}
                      data-tooltip={(task as any).visibility === 'admin_only'
                        ? 'Make visible to all'
                        : 'Make admin-only'}
                      data-testid={`button-pulse-visibility-${task.id}`}
                    >
                      {(task as any).visibility === 'admin_only'
                        ? <Lock className="w-3 h-3" />
                        : <Unlock className="w-3 h-3" />}
                    </span>
                  )}
                </button>
                {/* Sibling of the title button (NOT nested inside it) so it is
                    a standalone accessible control — see PreviewShareLink.
                    Task #189: also shown for final pulses. */}
                {isShareManagerPulse && <PreviewShareLink pulseId={task.id} shareCategory={task.category} fillRowHeight />}
                {linkedShare && (
                  <PreviewShareLink pulseId={linkedShare.sharePulseId} shareCategory={linkedShare.shareType} fillRowHeight />
                )}
              </div>
              
              {/* Progress bar for pulses with tasks */}
              {isPulse && taskProgress && taskProgress.hasTasks && (
                <div className="mt-0.5 ml-7 mr-3">
                  <div className="h-0.5 bg-gray-700 rounded-full overflow-hidden flex">
                    {/* Green section for Done tasks */}
                    {taskProgress.done > 0 && (
                      <div 
                        className="h-full transition-all duration-300"
                        style={{ 
                          width: `${taskProgress.done}%`,
                          backgroundColor: '#34d391'  // Same green as status buttons
                        }}
                      />
                    )}
                    {/* Blue section for Working on it tasks */}
                    {taskProgress.working > 0 && (
                      <div 
                        className="h-full transition-all duration-300"
                        style={{ 
                          width: `${taskProgress.working}%`,
                          backgroundColor: '#5f8ac9'  // Same blue as status buttons
                        }}
                      />
                    )}
                    {/* Dark grey section for other statuses */}
                    {taskProgress.other > 0 && (
                      <div 
                        className="h-full transition-all duration-300"
                        style={{ 
                          width: `${taskProgress.other}%`,
                          backgroundColor: '#374151'  // Dark grey
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              {isPulse && showBudgetColumn && task.category === 'admin' && (task as any).adminData?.subType === 'budgeting' && (
                <BudgetPulseDetail adminData={(task as any).adminData} />
              )}
            </div>
          </div>

          {/* One shared 2×2 tag grid in the Lead / Artist columns. */}
          <div className="col-span-2 flex items-center px-2 min-w-0">
            {isPulse && currentUser && (
              <PulseUserTags pulseId={task.id} projectId={projectId}
                users={tagUsers}
                states={pulseTreeData?.userTags || (task as any).userTags || []}
                viewerId={currentUser.id}
                viewerRole={currentUser.role === 'admin' || tagUsers.some(u => u.id === currentUser.id && u.assigned) ? currentUser.role : 'readonly'}
                restricted={task.category === 'admin' || (task as any).visibility === 'admin_only'}
                onChange={body => sendPulseTag(task.id, body)}
              />
            )}
          </div>

          {/* Project Status Column - Time tracking labels for pulses */}
          <div className="px-2">
            {shouldShowTimeTracking && <TimeTrackSummary projectId={projectId} targetType={isPulse ? "pulse" : "task"} targetId={task.id} />}
          </div>

          {/* Task/Pulse Status Column (hidden for milestones) */}
          <div className="px-2">
            {!isMilestone && (
              <EditableStatus 
                status={task.status}
                type={isPulse ? "pulse" : "task"}
                onStatusChange={isPulse ? handlePulseStatusChange : handleTaskStatusChange}
              />
            )}
          </div>

          {/* Timing Column (show creation date for milestones, due date for others) */}
          <div className="px-2">
            {isMilestone ? (
              <span className="text-sm">
                {task.dateAdded ? format(new Date(task.dateAdded), 'PP') : ''}
              </span>
            ) : (
              <TaskDatePicker 
                task={task}
                onDateChange={handleDueDateChange}
              />
            )}
          </div>

          {/* Actions Column */}
          <div className="flex items-center justify-end space-x-1">
            {/* Share button for pulses only (visible on hover) */}
            {isPulse && onShareClick && isHovered && task.category !== 'admin' && (
              <IconTooltip label="Share pulse">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-blue-400 p-1 h-auto"
                  title="Share pulse"
                  aria-label="Share pulse"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onShareClick(task as Pulse);
                  }}
                  data-testid={`button-share-pulse-${task.id}`}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </IconTooltip>
            )}
            {!isPulse && <TaskEditButton key={task.id} task={task as Task} hovered={isHovered} />}
            {/* Settings button for pulses only (visible on hover) */}
            {isPulse && onOpenSettings && isHovered && (
              <IconTooltip label="Pulse settings">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-orange-400 p-1 h-auto"
                  title="Pulse settings"
                  aria-label="Pulse settings"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onOpenSettings(task as Pulse);
                  }}
                  data-testid={`button-settings-pulse-${task.id}`}
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </IconTooltip>
            )}
            {shouldShowTimeTracking && <TimeTrackButton projectId={projectId} targetType={isPulse ? "pulse" : "task"} targetId={task.id} />}
            {task.message && (
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white p-1 h-auto"
                title="View Message"
                onClick={() => setShowMessageModal(true)}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
            {/* Eye icon removed per user request */}
          </div>
          {showBudgetColumn && <div />}
        </div>
      </div>

      {isClientFilesPulse && (
        <Dialog open={showClientUploadReview} onOpenChange={setShowClientUploadReview}>
          <DialogContent className="max-w-4xl max-h-[85dvh] overflow-y-auto bg-gray-900 border-gray-700 text-gray-200">
            <DialogHeader>
              <DialogTitle>Files from Client</DialogTitle>
              <DialogDescription>Nachricht und Dateien prüfen, freigeben oder ablehnen.</DialogDescription>
            </DialogHeader>
            {clientUploadSessionId
              ? <ClientUploadReview projectId={projectId} sessionId={clientUploadSessionId} />
              : <div className="p-4 text-sm text-amber-300">Diese Sendung hat keine gültige Sitzungsreferenz. Bitte die Projektansicht aktualisieren.</div>}
          </DialogContent>
        </Dialog>
      )}
      {task.message && (
        <MessageModal
          message={task.message}
          open={showMessageModal}
          onOpenChange={setShowMessageModal}
        />
      )}
      
      <TaskDetailDialog
        task={task}
        open={showTaskDetail}
        onOpenChange={setShowTaskDetail}
      />

      {/* Legacy ModernChat removed - V2 ChatDock is now the primary chat interface */}

      <PulseAttachmentPreviewModal
        pulseId={previewPulseId}
        attachment={previewAttachment}
        onClose={() => {
          setPreviewAttachment(null);
          setPreviewPulseId(null);
        }}
      />
    </>
  );
}
